import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  FileText, 
  Calendar, 
  User, 
  MoreVertical, 
  Play, 
  Download, 
  Edit, 
  Copy,
  Trash2,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react';

interface Report {
  id: string;
  name: string;
  description: string;
  report_type: string;
  status: 'completed' | 'generating' | 'draft' | 'failed' | 'active' | 'error';
  last_generated_at: string | null;
  schedule: string | null | { frequency?: string; [key: string]: any };
  config?: {
    widgets?: any[];
  };
  users?: {
    id: string;
    name: string;
    email: string;
  };
  projects?: {
    id: string;
    name: string;
    organization_id: string;
  };
  created_at: string;
  updated_at: string;
}

interface ReportCardProps {
  report: Report;
  onEdit?: (report: Report) => void;
  onDelete?: (report: Report) => void;
  onDuplicate?: (report: Report) => void;
  onGenerate?: (report: Report) => void;
  onDownload?: (report: Report, format?: string) => void;
}

export function ReportCard({ 
  report, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onGenerate, 
  onDownload 
}: ReportCardProps) {
  const navigate = useNavigate();
  
  // Helper function to determine if report is viewable (has generated data)
  const isReportViewable = (report: Report) => {
    return (report.status === 'active' || report.status === 'completed') && 
           report.last_generated_at !== null;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': 
      case 'active': return 'default' as const;
      case 'generating': return 'secondary' as const;
      case 'draft': return 'outline' as const;
      case 'failed': 
      case 'error': return 'destructive' as const;
      default: return 'secondary' as const;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'active': return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'generating': return <Loader className="w-3 h-3 mr-1 animate-spin" />;
      case 'draft': return <Edit className="w-3 h-3 mr-1" />;
      case 'failed':
      case 'error': return <AlertCircle className="w-3 h-3 mr-1" />;
      default: return <Clock className="w-3 h-3 mr-1" />;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'analytics': return '📊';
      case 'performance': return '⚡';
      case 'conversion': return '🎯';
      case 'audience': return '👥';
      case 'seo': return '🔍';
      case 'social': return '📱';
      case 'custom': return '⚙️';
      default: return '📋';
    }
  };

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-lg flex items-center justify-center">
            <span className="text-lg">{getTypeIcon(report.report_type)}</span>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{report.name}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{report.description}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge variant={getStatusColor(report.status)}>
            {getStatusIcon(report.status)}
            {isReportViewable(report) ? 'Ready' : 
             report.status.charAt(0).toUpperCase() + report.status.slice(1)}
          </Badge>
          
          <div className="relative group">
            <Button variant="ghost" size="sm">
              <MoreVertical className="w-4 h-4" />
            </Button>
            
            {/* Dropdown Menu */}
            <div className="absolute right-0 top-8 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              {isReportViewable(report) && (
                <button
                  onClick={() => navigate(`/reports/${report.id}`)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Report
                </button>
              )}
              <button
                onClick={() => onEdit?.(report)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Report
              </button>
              <button
                onClick={() => onDuplicate?.(report)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
              >
                <Copy className="w-4 h-4 mr-2" />
                Duplicate
              </button>
              {isReportViewable(report) && (
                <div className="relative group/export">
                  <button className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 flex items-center">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </button>
                  <div className="absolute left-full top-0 ml-1 w-36 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 opacity-0 invisible group-hover/export:opacity-100 group-hover/export:visible transition-all duration-200 z-20">
                    <button
                      onClick={() => onDownload?.(report, 'pdf')}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                    >
                      <FileText className="w-3 h-3 mr-2" />
                      PDF
                    </button>
                    <button
                      onClick={() => onDownload?.(report, 'csv')}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                    >
                      <FileText className="w-3 h-3 mr-2" />
                      CSV
                    </button>
                    <button
                      onClick={() => onDownload?.(report, 'xlsx')}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                    >
                      <FileText className="w-3 h-3 mr-2" />
                      Excel
                    </button>
                  </div>
                </div>
              )}
              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
              <button
                onClick={() => onDelete?.(report)}
                className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Details */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <FileText className="w-4 h-4 mr-1" />
            <span>{report.config?.widgets?.length || 0} widgets</span>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <User className="w-4 h-4 mr-1" />
            <span>{report.users?.name || 'Unknown'}</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4 mr-1" />
            <span>Last: {formatDate(report.last_generated_at)}</span>
          </div>
          {report.schedule && (
            <div className="flex items-center text-gray-600 dark:text-gray-400">
              <Calendar className="w-4 h-4 mr-1" />
              <span className="capitalize">
                {typeof report.schedule === 'string' 
                  ? report.schedule 
                  : (report.schedule?.frequency || 'Scheduled')
                }
              </span>
            </div>
                      )}
          </div>
        </div>

      {/* Actions */}
      <div className="flex space-x-2">
        {report.status === 'draft' ? (
          <Button 
            size="sm" 
            className="flex-1"
            onClick={() => onEdit?.(report)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Continue Editing
          </Button>
        ) : report.status === 'generating' ? (
          <Button size="sm" className="flex-1" disabled>
            <Loader className="w-4 h-4 mr-2 animate-spin" />
            Generating...
          </Button>
        ) : (
          <>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => onGenerate?.(report)}
            >
              <Play className="w-4 h-4 mr-2" />
              Generate
            </Button>
            {isReportViewable(report) && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/reports/${report.id}`)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onDownload?.(report, 'pdf')}
                >
                  <Download className="w-4 h-4" />
                </Button>
              </>
            )}
          </>
        )}
      </div>

      {/* Progress Bar for Generating Status */}
      {report.status === 'generating' && (
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
            <span>Generating report...</span>
            <span>75%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: '75%' }}
            ></div>
          </div>
        </div>
      )}

      {/* Error Message for Failed Status */}
      {(report.status === 'failed' || report.status === 'error') && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mr-2" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">Generation failed</p>
              <p className="text-xs text-red-600 dark:text-red-400">Please try again or contact support</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              onClick={() => onGenerate?.(report)}
            >
              Retry
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
} 